import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Check, ChevronLeft, ChevronRight } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { PortalModal } from '@/components/portal/PortalModal'
import { PortalField } from '@/components/portal/PortalField'
import { PortalSelect } from '@/components/portal/PortalSelect'
import { PasswordInput } from '@/components/portal/PasswordInput'
import { Checkbox } from '@/components/ui/checkbox'
import { useAccountOptions } from '@/hooks/useAccountOptions'
import { useAuthStore } from '@/store/auth'
import { clientApi } from '@/lib/api-client'
import { ensureClientProfile } from '@/lib/client-id'
import { accountTypeLabel } from '@/components/portal/account-type-label'

interface LiveAccountModalProps {
  open: boolean
  onClose: () => void
}

interface LiveModalForm {
  account_type: string
  exchange_rate: number
  leverage: string
  leverage_policy_accepted: boolean
}

type LiveStep = 1 | 2

/* A controlled select must list the value that will be submitted, even if the server's options omit it. */
function withCurrent(options: string[], current: string) {
  return current && !options.includes(current) ? [current, ...options] : options
}

export function LiveAccountModal({ open, onClose }: LiveAccountModalProps) {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const queryClient = useQueryClient()
  const { data: options } = useAccountOptions()
  const [error, setError] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordBlur, setPasswordBlur] = useState(false)
  const [confirmBlur, setConfirmBlur] = useState(false)
  const [attempted, setAttempted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [step, setStep] = useState<LiveStep>(1)
  const stepHeadingRef = useRef<HTMLHeadingElement>(null)
  const stepMoved = useRef(false)
  const passwordError = (attempted || passwordBlur) && !password.trim() ? t('validation.required') : undefined
  const confirmError = (attempted || confirmBlur) && !confirmPassword.trim() ? t('validation.required') : undefined

  const { handleSubmit, setValue, watch } = useForm<LiveModalForm>({
    defaultValues: {
      account_type: 'ZERO',
      exchange_rate: 10000,
      leverage: '1:100',
      leverage_policy_accepted: false,
    },
  })

  // Every opening starts at step 1; the entered values stay as they did before.
  useEffect(() => {
    if (open) setStep(1)
  }, [open])

  // After a step change: body back to the top, focus on the step heading.
  useEffect(() => {
    if (!stepMoved.current) return
    stepMoved.current = false
    const heading = stepHeadingRef.current
    heading?.closest('.prod-modal__body')?.scrollTo({ top: 0 })
    heading?.focus({ preventScroll: true })
  }, [step])

  function goToStep(next: LiveStep) {
    stepMoved.current = true
    setStep(next)
  }

  async function onSubmit(data: LiveModalForm) {
    setError(null)
    setAttempted(true)
    if (!password.trim() || !confirmPassword.trim()) return
    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'))
      return
    }
    if (!data.leverage_policy_accepted) {
      setError(t('wizard.leveragePolicy'))
      return
    }
    const clientId = await ensureClientProfile(user)
    if (!clientId) {
      setError(t('live.clientRequired'))
      return
    }
    setSubmitting(true)
    try {
      await clientApi.createLiveAccount(clientId, {
        account_type: data.account_type,
        exchange_rate: data.exchange_rate,
        leverage: data.leverage,
        mt_password: password,
        leverage_policy_accepted: true,
      })
      void queryClient.invalidateQueries({ queryKey: ['client', 'status'] })
      void queryClient.invalidateQueries({ queryKey: ['client', 'accounts'] })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'))
    } finally {
      setSubmitting(false)
    }
  }

  const features = [
    t('live.features.kyc'),
    t('live.features.commission'),
    t('live.features.spread'),
    t('live.features.lot'),
  ]

  const stepTitle = step === 1 ? t('live.stepChoose') : t('live.stepSecurity')

  return (
    <PortalModal
      open={open}
      onClose={onClose}
      title={t('live.title')}
      meta={
        <div className="prod-sheet-steps">
          <p className="prod-sheet-steps__count">
            {t('live.stepCount', { current: step, total: 2, step: stepTitle })}
          </p>
          <div className="prod-segments" aria-hidden="true">
            <span className={`prod-segments__item ${step === 1 ? 'prod-segments__item--current' : 'prod-segments__item--done'}`} />
            <span className={`prod-segments__item${step === 2 ? ' prod-segments__item--current' : ''}`} />
          </div>
        </div>
      }
      footer={
        step === 1 ? (
          <button type="button" className="prod-btn prod-btn--gold" onClick={() => goToStep(2)}>
            {t('live.next')}
            <ChevronRight className="size-4" aria-hidden="true" />
          </button>
        ) : (
          <>
            <button type="button" className="prod-btn prod-btn--outline prod-sheet-back" onClick={() => goToStep(1)}>
              <ChevronLeft className="size-4" aria-hidden="true" />
              {t('common.back')}
            </button>
            <button
              type="submit"
              form="live-account-form"
              className="prod-btn prod-btn--gold"
              disabled={submitting}
            >
              {t('live.create')}
            </button>
          </>
        )
      }
    >
      <form
        id="live-account-form"
        onSubmit={(e) => {
          // Enter on step 1 moves on; only step 2 submits.
          if (step === 1) {
            e.preventDefault()
            goToStep(2)
            return
          }
          void handleSubmit(onSubmit)(e)
        }}
      >
        <h3 ref={stepHeadingRef} tabIndex={-1} className="prod-sheet-step-title">
          {stepTitle}
        </h3>

        {step === 1 ? (
          <>
            <p className="prod-modal__subtitle">{t('live.subtitle')}</p>
            <fieldset className="prod-choice-field">
              <legend className="prod-field__label prod-field__label--required">{t('wizard.accountType')}</legend>
              <div className="prod-radio-group prod-radio-group--types">
                {(options?.account_types ?? ['ZERO']).map((type) => (
                  <label key={type} className="prod-radio prod-radio--card">
                    <input
                      type="radio"
                      name="live-account-type"
                      value={type}
                      checked={watch('account_type') === type}
                      onChange={() => setValue('account_type', type)}
                    />
                    <span className="prod-radio__body">
                      <span className="prod-radio__caption">MT5:</span>
                      <span className="prod-radio__title prod-radio__title--nowrap">{accountTypeLabel(type)}</span>
                    </span>
                  </label>
                ))}
              </div>
              <ul className="prod-feature-list">
                {features.map((f) => (
                  <li key={f} className="prod-feature-list__item">
                    <Check className="size-3" aria-hidden="true" /> {f}
                  </li>
                ))}
              </ul>
              <p className="prod-warn">{t('live.leverageWarning')}</p>
            </fieldset>

            <fieldset className="prod-choice-field">
              <legend className="prod-field__label prod-field__label--required">{t('wizard.exchangeRate')}</legend>
              <div className="prod-radio-group prod-radio-group--grid">
                {(options?.exchange_rates ?? [10000]).map((rate) => (
                  <label key={rate} className="prod-radio prod-radio--card">
                    <input
                      type="radio"
                      name="live-exchange-rate"
                      value={rate}
                      checked={watch('exchange_rate') === rate}
                      onChange={() => setValue('exchange_rate', Number(rate))}
                    />
                    <span className="prod-radio__body">
                      <span className="prod-radio__caption">Fixed Rate</span>
                      <span className="prod-radio__title prod-radio__title--num">{rate.toLocaleString('id-ID')}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <PortalField label={t('wizard.leverage')} required>
              <PortalSelect value={watch('leverage')} onChange={(e) => setValue('leverage', e.target.value)}>
                {withCurrent(options?.leverage_options ?? ['1:100'], watch('leverage')).map((lev) => (
                  <option key={lev} value={lev}>
                    {lev.replace(':', ' : ')}
                  </option>
                ))}
              </PortalSelect>
            </PortalField>
          </>
        ) : (
          <>
            <p className="prod-modal__subtitle">{t('live.securityHint')}</p>
            <PortalField label={t('wizard.mtPassword')} required error={passwordError}>
              <PasswordInput
                value={password}
                autoComplete="new-password"
                onChange={setPassword}
                onBlur={() => setPasswordBlur(true)}
                showCriteria
              />
            </PortalField>

            <PortalField label={t('live.confirmPassword')} required error={confirmError}>
              <PasswordInput
                value={confirmPassword}
                autoComplete="new-password"
                onChange={setConfirmPassword}
                onBlur={() => setConfirmBlur(true)}
              />
            </PortalField>

            <div className="prod-field prod-mb-lg">
              <Checkbox
                label={t('live.leveragePolicy')}
                checked={watch('leverage_policy_accepted')}
                onChange={(e) => setValue('leverage_policy_accepted', e.target.checked)}
              />
            </div>
          </>
        )}

        {/* Submit errors (mismatch, policy, client profile, API) are form-level, so they show on whichever step is open. */}
        {error ? (
          <p className="prod-error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </PortalModal>
  )
}
